import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@/shared/i18n/useT';
import { PageHeader } from '@/shared/components/PageHeader';
import {
  BookingApiError,
  bookingErrorKey,
  cancelBooking,
  icsUrl,
  rescheduleBooking,
  fetchBookingByToken,
} from '@/client/booking/api';
import { ErrorNotice } from '@/client/booking/ErrorNotice';
import { SlotPicker } from '@/client/booking/SlotPicker';
import { useBookingT, type BookingMessageKey } from '@/client/booking/i18n';
import { monthKeyInZone, visitorTimeZone, type DayKey, type MonthKey } from '@/client/booking/datetime';
import type { BookingRecord } from '@/client/booking/types';

type PageState = 'loading' | 'ready' | 'notFound' | 'error';
type Mode = 'view' | 'cancel' | 'reschedule';

/**
 * `/bookings/manage/:token` — the screen the confirmation email and the
 * post-booking confirmation link both point at. Reachable with the token
 * alone, so nothing here requires a session.
 */
export function BookingManage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const id = params.get('id');
  const t = useBookingT();
  const { formatLocale } = useI18n();

  const [state, setState] = useState<PageState>('loading');
  const [loadErrorKey, setLoadErrorKey] = useState<BookingMessageKey>('publicBooking.error.bookingNotFound');
  const [booking, setBooking] = useState<BookingRecord | null>(null);
  const [mode, setMode] = useState<Mode>('view');
  const [busy, setBusy] = useState(false);
  const [actionErrorKey, setActionErrorKey] = useState<BookingMessageKey | null>(null);
  const [reason, setReason] = useState('');
  const [cancelled, setCancelled] = useState(false);
  const [rescheduled, setRescheduled] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);

  const visitorZone = useMemo(() => visitorTimeZone(), []);
  const [month, setMonth] = useState<MonthKey>(() => monthKeyInZone(new Date(), visitorZone));
  const [selectedDay, setSelectedDay] = useState<DayKey | null>(null);

  useEffect(() => {
    if (!token) { setState('notFound'); return; }
    let active = true;
    setState('loading');
    void fetchBookingByToken(token, id)
      .then((record) => { if (active) { setBooking(record); setState('ready'); } })
      .catch((error: unknown) => {
        if (!active) return;
        setLoadErrorKey(bookingErrorKey(error));
        setState(error instanceof BookingApiError && error.status === 404 ? 'notFound' : 'error');
      });
    return () => { active = false; };
  }, [id, token]);

  // `dateStyle`/`timeStyle` cannot be combined with individual component
  // options — ECMA-402 throws a TypeError at construction.
  const dateFormat = useMemo(
    () => new Intl.DateTimeFormat(formatLocale, {
      dateStyle: 'full', timeStyle: 'short', timeZone: booking?.timezone ?? visitorZone,
    }),
    [booking?.timezone, formatLocale, visitorZone],
  );

  const confirmCancel = async () => {
    if (!booking || !token || busy) return;
    setBusy(true);
    setActionErrorKey(null);
    try {
      await cancelBooking(booking.id, token, reason.trim());
      setCancelled(true);
      setMode('view');
    } catch (error: unknown) {
      setActionErrorKey(bookingErrorKey(error));
    } finally {
      setBusy(false);
    }
  };

  const confirmReschedule = async () => {
    if (!booking || !token || !selectedSlot || busy) return;
    setBusy(true);
    setActionErrorKey(null);
    try {
      await rescheduleBooking(booking.id, token, selectedSlot);
      setBooking({ ...booking, startsAt: selectedSlot });
      setRescheduled(true);
      setMode('view');
    } catch (error: unknown) {
      setActionErrorKey(bookingErrorKey(error));
    } finally {
      setBusy(false);
    }
  };

  if (state === 'loading') {
    return <p role="status" className="text-sm text-muted">{t('common.loading')}</p>;
  }

  if (state === 'notFound' || state === 'error' || !booking) {
    return (
      <div className="mx-auto max-w-xl">
        <PageHeader title={t('publicBooking.manage.title')} description={t(loadErrorKey)} />
      </div>
    );
  }

  const isActionable = booking.status === 'pending' || booking.status === 'confirmed';

  return (
    <div className="mx-auto max-w-2xl">
      <PageHeader title={t('publicBooking.manage.title')} description={t('publicBooking.manage.description')} />

      {cancelled && (
        <div className="mb-4 rounded-token border border-border bg-surface-2 p-4 sm:p-5">
          <h2 className="text-base font-semibold text-fg">{t('publicBooking.manage.cancelledTitle')}</h2>
          <p className="mt-1 text-sm text-muted">{t('publicBooking.manage.cancelledBody')}</p>
        </div>
      )}
      {rescheduled && !cancelled && (
        <div className="mb-4 rounded-token border border-success bg-success-soft p-4 sm:p-5">
          <h2 className="text-base font-semibold text-fg">{t('publicBooking.manage.rescheduledTitle')}</h2>
          <p className="mt-1 text-sm text-fg">{dateFormat.format(new Date(booking.startsAt))}</p>
        </div>
      )}

      {!cancelled && (
        <div className="rounded-token border border-border bg-surface-2 p-4 sm:p-5">
          <p className="text-base font-semibold text-fg">{booking.title}</p>
          <p className="mt-1 text-sm text-fg">{t('publicBooking.manage.current')}: {dateFormat.format(new Date(booking.startsAt))}</p>
          <p className="mt-1 text-sm text-muted">{booking.pageTitle}</p>
          {booking.meetingUrl && (
            <div className="mt-3 flex items-center gap-2">
              <a
                href={booking.meetingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="tap-target inline-flex items-center rounded-full bg-accent px-4 py-1.5 text-xs font-semibold text-accent-fg hover:opacity-90"
              >
                Join Video Meeting
              </a>
              <button
                type="button"
                onClick={() => void navigator.clipboard.writeText(booking.meetingUrl!)}
                className="tap-target rounded-full border border-border px-3 py-1 text-xs text-muted hover:text-fg"
              >
                Copy Link
              </button>
            </div>
          )}
          {booking.intakeAnswers && Object.keys(booking.intakeAnswers).length > 0 && (
            <div className="mt-3 border-t border-border pt-2 text-xs space-y-1">
              <span className="font-semibold text-fg">Submitted details:</span>
              {Object.entries(booking.intakeAnswers).map(([k, v]) => (
                <div key={k} className="text-muted">
                  <span className="font-medium text-fg">{k}:</span>{' '}
                  {Array.isArray(v) ? v.join(', ') : String(v ?? '')}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {actionErrorKey && <div className="mt-4"><ErrorNotice message={t(actionErrorKey)} /></div>}

      {!cancelled && !isActionable && (
        <p className="mt-4 text-sm text-muted">{t('publicBooking.manage.alreadyCancelled')}</p>
      )}

      {!cancelled && isActionable && mode === 'view' && (
        <div className="mt-4 flex flex-wrap gap-2">
          <a href={icsUrl(booking.id, token ?? null)} className="tap-target rounded-full border border-border-strong px-4 text-sm text-fg">
            {t('publicBooking.confirmed.addToCalendar')}
          </a>
          <button type="button" onClick={() => setMode('reschedule')} className="tap-target rounded-full border border-border-strong px-4 text-sm text-fg">
            {t('publicBooking.manage.reschedule')}
          </button>
          <button type="button" onClick={() => setMode('cancel')} className="tap-target rounded-full border border-danger px-4 text-sm text-danger">
            {t('publicBooking.manage.cancel')}
          </button>
        </div>
      )}

      {mode === 'cancel' && (
        <div className="mt-4 rounded-xl border border-border bg-surface-2 p-4 sm:p-5">
          <h2 className="text-base font-semibold text-fg">{t('publicBooking.manage.cancelTitle')}</h2>
          <label className="mt-3 block text-sm text-muted">
            {t('publicBooking.manage.cancelReason')}
            <textarea
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              placeholder={t('publicBooking.manage.cancelReasonPlaceholder')}
              className="mt-1 w-full radius-box border border-border-strong bg-surface px-3 py-2 text-sm text-fg"
            />
          </label>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={() => setMode('view')} className="tap-target rounded-full border border-border px-4 text-sm text-fg">
              {t('publicBooking.manage.keep')}
            </button>
            <button type="button" disabled={busy} onClick={() => void confirmCancel()} className="tap-target rounded-full bg-danger px-4 text-sm font-medium text-danger-fg disabled:opacity-60">
              {busy ? t('common.loading') : t('publicBooking.manage.cancelConfirm')}
            </button>
          </div>
        </div>
      )}

      {mode === 'reschedule' && (
        <div className="mt-4 rounded-xl border border-border bg-surface-2 p-4 sm:p-5">
          <h2 className="text-base font-semibold text-fg">{t('publicBooking.manage.rescheduleTitle')}</h2>
          <div className="mt-3">
            <SlotPicker
              slug={booking.pageSlug ?? ''}
              eventTypeId={booking.eventTypeId ?? ''}
              professionalZone={booking.timezone}
              visitorZone={visitorZone}
              timeZone={booking.timezone}
              onTimeZoneChange={() => {}}
              month={month}
              onMonthChange={setMonth}
              selectedDay={selectedDay}
              onSelectDay={setSelectedDay}
              selectedSlot={selectedSlot}
              onSelectSlot={setSelectedSlot}
            />
          </div>
          <div className="mt-3 flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={() => { setMode('view'); setSelectedSlot(null); }} className="tap-target rounded-full border border-border px-4 text-sm text-fg">
              {t('common.back')}
            </button>
            <button type="button" disabled={!selectedSlot || busy} onClick={() => void confirmReschedule()} className="tap-target flex-1 rounded-full bg-accent px-4 text-sm font-medium text-accent-fg disabled:opacity-60">
              {busy ? t('common.loading') : t('publicBooking.manage.rescheduleConfirm')}
            </button>
          </div>
        </div>
      )}

      {booking.pageSlug && (
        <Link to={`/${encodeURIComponent(booking.pageSlug)}`} className="tap-target mt-5 inline-flex items-center text-sm text-accent underline">
          {t('publicBooking.confirmed.bookAnother')}
        </Link>
      )}
    </div>
  );
}
